import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { BillingAddressInput } from '../../models/billing-address.model';
import { AddressAutocompleteDirective } from '../address-autocomplete/address-autocomplete.directive';
import { ALL_COUNTRIES, Country } from '../country-dropdown/countries.model';
import { CountrySelectDirective } from '../country-dropdown/country-select.directive';

export const COUNTRY_US = 'US';
export const COUNTRY_CA = 'CA';
export const ZIP_MAX_LENGTH = 20;
const TEXT_MAX_LENGTH = 255;
const ZIP_PATTERN_US = /^\d{5}(-\d{4})?$/;
const ZIP_PATTERN_CA = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
const CA_ZIP_INNER_SPACE_INDEX = 3;

function normalizeCountry(country: string | null | undefined): string {
  return String(country ?? '').trim().toUpperCase();
}

/** Valida el código postal según país: US/CA obligatorio con formato; otros opcional (máx. 20). */
export function zipCodeValidator(country: string | null | undefined): ValidatorFn {
  const code = normalizeCountry(country);
  return (control: AbstractControl): ValidationErrors | null => {
    const value = String(control.value ?? '').trim();
    if (code === COUNTRY_US || code === COUNTRY_CA) {
      if (!value) return { required: true };
      const pattern = code === COUNTRY_US ? ZIP_PATTERN_US : ZIP_PATTERN_CA;
      return pattern.test(value) ? null : { pattern: true };
    }
    return value.length > ZIP_MAX_LENGTH ? { maxlength: true } : null;
  };
}

/** CA: sin espacios, mayúscula y con espacio central (A1A 1A1). Otros países: trim. */
export function normalizeZipCode(country: string, zip: string | null | undefined): string {
  const value = String(zip ?? '').trim();
  if (normalizeCountry(country) !== COUNTRY_CA || !ZIP_PATTERN_CA.test(value)) {
    return value;
  }
  const compact = value.replace(/\s+/g, '').toUpperCase();
  return `${compact.slice(0, CA_ZIP_INNER_SPACE_INDEX)} ${compact.slice(CA_ZIP_INNER_SPACE_INDEX)}`;
}

let nextFormId = 0;

@Component({
  selector: 'app-billing-address-form',
  templateUrl: './billing-address-form.component.html',
  styleUrls: ['./billing-address-form.component.scss'],
  standalone: true,
  imports: [ReactiveFormsModule, TranslateModule, CountrySelectDirective, AddressAutocompleteDirective]
})
export class BillingAddressFormComponent implements OnInit, OnDestroy {
  /** Deshabilita el botón de guardar mientras el padre envía la dirección. */
  @Input() submitting = false;
  /** Emite la dirección válida y normalizada. No llama al backend: el padre la crea. */
  @Output() saved = new EventEmitter<BillingAddressInput>();
  @Output() cancel = new EventEmitter<void>();

  /** Sufijo único por instancia para los ids de los campos (evita ids duplicados en el DOM). */
  readonly uid = nextFormId++;
  readonly countries: Country[] = ALL_COUNTRIES;
  readonly zipMaxLength = ZIP_MAX_LENGTH;
  readonly textMaxLength = TEXT_MAX_LENGTH;

  form = new FormGroup({
    country: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    address: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(TEXT_MAX_LENGTH)] }),
    state: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(TEXT_MAX_LENGTH)] }),
    city: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(TEXT_MAX_LENGTH)] }),
    zipCode: new FormControl('', { nonNullable: true, validators: [zipCodeValidator('')] })
  });

  private countrySub?: Subscription;

  ngOnInit(): void {
    this.countrySub = this.form.controls.country.valueChanges.subscribe(country => this.applyZipValidator(country));
  }

  ngOnDestroy(): void {
    this.countrySub?.unsubscribe();
  }

  get isZipRequired(): boolean {
    const country = normalizeCountry(this.form.controls.country.value);
    return country === COUNTRY_US || country === COUNTRY_CA;
  }

  get zipInvalidMessageKey(): string {
    return normalizeCountry(this.form.controls.country.value) === COUNTRY_CA
      ? 'customerCards.zipInvalidCa'
      : 'customerCards.zipInvalidUs';
  }

  showError(name: 'country' | 'address' | 'state' | 'city' | 'zipCode'): boolean {
    const control = this.form.controls[name];
    return control.invalid && (control.touched || control.dirty);
  }

  submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    this.saved.emit(this.toInput());
  }

  onCancel(): void {
    this.cancel.emit();
  }

  private toInput(): BillingAddressInput {
    const value = this.form.getRawValue();
    const country = normalizeCountry(value.country);
    const input: BillingAddressInput = {
      address: value.address.trim(),
      country,
      state: value.state.trim(),
      city: value.city.trim()
    };
    const zipCode = normalizeZipCode(country, value.zipCode);
    if (zipCode) {
      input.zipCode = zipCode;
    }
    return input;
  }

  private applyZipValidator(country: string): void {
    const zip = this.form.controls.zipCode;
    zip.setValidators(zipCodeValidator(country));
    zip.updateValueAndValidity();
  }
}
